import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftCircle } from "lucide-react";
import toast from "react-hot-toast";

const GameDetailsPage = () => {
    const location = useLocation();
    const { id } = useParams(); // this is slug or game id from URL
    const navigate = useNavigate();
    const { game, abc, xyz, selectedPlan } = location.state || {};
    const [coinValue, setCoinValue] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [gems, setGems] = useState("");
    const [currencyAmount, setCurrencyAmount] = useState("");
    const [userId, setUserId] = useState("");

    // submit form
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isFormValid = gems && userId;

    const handleBuyNow = async () => {
        try {
            setIsSubmitting(true);
            setError("");

            const payload = {
                xyz,
                abc,
                gameId: id, // from params
                coin: gems, // gems entered
                receiverCoin: currencyAmount || game.price, // receiving coin
                userGameId: userId, // entered userId
            };

            // console.log("Submitting purchase:", payload);

            const response = await fetch(`https://api.gamegems.co/web/games/purchase-history`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (data.status === true) {
                toast.success("Purchase successful!");
                setCoinValue((prev) => (prev !== null ? prev - Number(gems) : prev));
                setGems("");
                setCurrencyAmount("");
                setUserId("");
            } else {
                toast.error(data.message || "Purchase failed.");
            }
        } catch (err) {
            toast.error("Something went wrong.");
        } finally {
            setIsSubmitting(false);
        }
    };


    useEffect(() => {
        // Find game details
        const fetchGameDetails = async () => {
            try {
                setLoading(true);
                setError("");
                
                // If abc & xyz not in state, also check query params
                const searchParams = new URLSearchParams(location.search);
                const abcParam = abc || searchParams.get("abc");
                const xyzParam = xyz || searchParams.get("xyz");

                if (!abcParam || !xyzParam) {
                    setError("Missing abc or xyz parameter in URL. Redirecting to game list...");
                    setTimeout(() => {
                        navigate('/game');
                    }, 2000);
                    setLoading(false);
                    return;
                }

                const res = await fetch(
                    `https://api.gamegems.co/web/games/list?abc=${abcParam}&xyz=${xyzParam}`
                );
                const data = await res.json();

                if (data.status && data.message === "success") {
                    // setGame(data.data);
                } else {
                    // Check if link has expired
                    if (data.message && (
                        data.message.toLowerCase().includes('expired') || 
                        data.message.toLowerCase().includes('invalid') ||
                        data.message.toLowerCase().includes('not found') ||
                        data.status === false
                    )) {
                        setError("Link has expired. Redirecting to game list...");
                        setTimeout(() => {
                            navigate('/game');
                        }, 2000);
                    } else {
                        setError("Game not found.");
                    }
                }
            } catch (err) {
                setError("Failed to load game details. Link may have expired. Redirecting to game list...");
                setTimeout(() => {
                    navigate('/game');
                }, 2000);
            } finally {
                setLoading(false);
            }
        };
        fetchGameDetails();

        // Find coin value
        const verifyGame = async () => {
            try {
                setLoading(true);
                setError("");

                // If abc & xyz not in state, also check query params
                const searchParams = new URLSearchParams(location.search);
                const abcParam = abc || searchParams.get("abc");
                const xyzParam = xyz || searchParams.get("xyz");

                if (!abcParam || !xyzParam) {
                    setError("Missing abc/xyz parameters.");
                    // Redirect to game list if parameters are missing
                    setTimeout(() => {
                        navigate('/game');
                    }, 2000);
                    return;
                }

                const response = await fetch("https://api.gamegems.co/web/games/verify", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ abc: abcParam, xyz: xyzParam }),
                });

                if (!response.ok) throw new Error("Verification failed");

                const data = await response.json();

                if (data.status && data.message === "success") {
                    setCoinValue(data.data?.coin || 0); // 👈 Adjust key if API uses different field
                } else {
                    // Check if link has expired
                    if (data.message && (
                        data.message.toLowerCase().includes('expired') || 
                        data.message.toLowerCase().includes('invalid') ||
                        data.message.toLowerCase().includes('not found') ||
                        data.status === false
                    )) {
                        setError("Link has expired. Redirecting to game list...");
                        setTimeout(() => {
                            navigate('/game');
                        }, 2000);
                    } else {
                        setError(data.message || "Failed to verify.");
                    }
                }
            } catch (err) {
                // Handle network errors or other issues that might indicate expired link
                setError("Connection failed. Link may have expired. Redirecting to game list...");
                setTimeout(() => {
                    navigate('/game');
                }, 2000);
            } finally {
                setLoading(false);
            }
        };
        verifyGame();


    }, [id, abc, xyz, location.search]);

    // Pre-populate fields if coming from selected plan
    useEffect(() => {
        if (selectedPlan) {
            setGems(selectedPlan.coin || "");
            setCurrencyAmount(selectedPlan.receiverCoin || "");
        }
    }, [selectedPlan]);

    // Calculate currency amount (only if not from selected plan)
    useEffect(() => {
        if (!selectedPlan && gems && game?.price) {
            setCurrencyAmount(gems / game.price);
        } else if (!selectedPlan) {
            setCurrencyAmount("");
        }
    }, [gems, game?.price, selectedPlan]);



    if (loading) return <p className="text-center text-white p-6">Loading...</p>;
    if (!game) return null;

    return (
        <div className="flex bg-gray-900 min-h-screen">
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="bg-gray-800 shadow-lg">
                    <div className="max-w-4xl mx-auto px-6 py-4">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center text-gray-400 hover:text-white transition-colors"
                            >
                                <ArrowLeftCircle className="h-6 w-6 mr-2" />
                            </button>
                            <div className="flex items-center bg-gray-700 px-4 py-2 rounded-lg">
                                <span className="text-yellow-400 mr-2">💎</span>
                                <span className="text-white font-semibold">
                                    Total Gems: <span className="text-green-400">{coinValue !== null ? Number(coinValue).toFixed(2) : '0.00'}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 p-6">
                    <div className="max-w-2xl mx-auto">
                        {/* Game Title */}
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-white mb-2">
                                {game.name}
                            </h1>
                            <p className="text-lg text-gray-300">
                                Buy <span className="text-green-400 font-semibold">{game.currencyField}</span>
                            </p>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 mb-6">
                                <p className="text-red-200 text-center">{error}</p>
                            </div>
                        )}

                        {/* Purchase Form */}
                        <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700">
                            <div className="p-4">
                                <form className="space-y-6">
                                    {/* Gems Input */}
                                    <div>
                                        <label className="block text-gray-300 font-medium mb-2" htmlFor="gems">
                                            <span className="flex items-center">
                                                Gems to Spend
                                                {/* {selectedPlan && (
                                                    <span className="ml-2 px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded-full">
                                                        From Selected Plan
                                                    </span>
                                                )} */}
                                            </span>
                                        </label>
                                        <input
                                            type="number"
                                            placeholder="Enter gems amount"
                                            value={gems}
                                            onChange={(e) => setGems(e.target.value)}
                                            readOnly={!!selectedPlan}
                                            className={`w-full text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 p-4 rounded-lg border transition-all ${
                                                selectedPlan 
                                                    ? 'bg-gray-600 border-gray-500 cursor-not-allowed' 
                                                    : 'bg-gray-700 border-gray-600 hover:border-gray-500'
                                            }`}
                                        />
                                    </div>

                                    {/* Receive Amount */}
                                    <div>
                                        <label className="block text-gray-300 font-medium mb-2" htmlFor="currencyAmount">
                                            <span className="flex items-center">
                                                You Will Receive {game.labelName}
                                                {/* {selectedPlan && (
                                                    <span className="ml-2 px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded-full">
                                                        From Selected Plan
                                                    </span>
                                                )} */}
                                            </span>
                                        </label>
                                        <input
                                            type="number"
                                            placeholder={`Receive ${game.labelName}`}
                                            value={currencyAmount}
                                            readOnly
                                            className="w-full bg-gray-600 border border-gray-500 text-white focus:outline-none p-4 rounded-lg cursor-not-allowed"
                                        />
                                    </div>

                                    {/* User Game ID */}
                                    <div>
                                        <label className="block text-gray-300 font-medium mb-2" htmlFor="userId">
                                            <span className="flex items-center">
                                                <span className="text-blue-400 mr-2">👤 </span>
                                                {game.formLabelName}
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder={game.formLabelName}
                                            value={userId}
                                            onChange={(e) => setUserId(e.target.value)}
                                            className="w-full bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-gray-500 p-4 rounded-lg transition-all"
                                        />
                                    </div>
                                    
                                    {/* Buy Button */}
                                    <button
                                        type="button"
                                        onClick={handleBuyNow}
                                        className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 ${
                                            isFormValid && !isSubmitting
                                                ? "bg-gradient text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                                                : "bg-gray-600 text-gray-400 cursor-not-allowed"
                                        }`}
                                        disabled={!isFormValid || isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center justify-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Processing...
                                            </span>
                                        ) : (
                                            <span className="flex items-center justify-center">
                                                Buy Now
                                            </span>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>

                        {/* Info Card */}
                        <div className="mt-6 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                            <div className="flex items-start">
                                <span className="text-blue-400 mr-3 mt-1">ℹ️</span>
                                <div className="text-sm text-blue-200">
                                    <p className="font-medium mb-1">Transaction Information</p>
                                    <p>Your gems will be converted to {game.labelName} and delivered to your game account. Please ensure your {game.userGameId} is correct.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GameDetailsPage;
